<template>
  <div class="grid-container grid-rows-[min-content_auto] md:mt-6 md:mb-12">
    <aside class="col-span-full md:col-start-2 md:col-end-6 xl:col-span-4">
      <div class="md:p-4 md:elevation md:rounded-lg md:border md:border-outline/16">
        <!-- ProfileDetailsSection -->
        <div class="relative md:p-1">
          <ProfileBanner class="md:hidden" />
          <ProfilePicture
            with-border
            :src="user.profile_image"
            :size="$window.width <= 905 ? 'medium' : 'large'"
            class="absolute right-1/2 top-1/2 translate-x-1/2 hover:shadow-none md:static md:flex md:justify-center md:transform-none"
          />
        </div>
        <div class="mt-10 md:m-0">
          <div class="md:flex md:flex-col md:gap-2 md:p-2">
            <h1 class="headline-sm text-center md:headline-md">{{ fullname }}</h1>
            <h2 class="title-md text-center md:title-lg md:font-normal">{{ user.headline }}</h2>
          </div>
          <!-- Social icons -->
          <div class="flex gap-4 p-2 justify-center fill-primary md:justify-evenly md:p-2">
            <a
              v-for="(item, key) in userSocialMedia"
              :key="key"
              :href="item.found_value"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ButtonIcon :icon="item.icon" class="btn-text" />
            </a>
          </div>
          <!-- Navigation buttons -->
          <div
            class="flex gap-[2px] overflow-x-scroll p-1 sm:justify-center md:flex-col md:overflow-auto md:p-0"
          >
            <ListItemWithDivider :with-divider="$window.width >= 905">
              <Button
                class="btn-text fill-primary min-w-fit md:w-full"
                icon="account-circle-filled"
                @click="currentTab = 'main'"
                >Perfil</Button
              >
            </ListItemWithDivider>
            <ListItemWithDivider v-if="user.has_cv_file" :with-divider="$window.width >= 905">
              <a
                @click="$modal.show('modal_cv')"
                href="#"
                class="btn-text fill-primary min-w-fit md:w-full !flex gap-2 justify-center items-center"
                icon="file-download"
              >
                <Icon name="eye" />
                <span>
                  Curriculum Vitae
                </span>
              </a>
              <modal
                  name="modal_cv"
                  height="auto"
                  :clickToClose="false"
                  reset
                  adaptive
                  scrollable
                  classes="!surface-1 md:!rounded-xl !overflow-y-auto"
                  @close="$modal.hide('modal_cv')"
                >
                  <div class="card row p-2">    
                    <div class="col-md-12">
                      <section>
                        <h3 class="headline-5 md:headline-sm">Curriculum Vitae
                           <a
                              :href="$route('cv')"
                              target="_blank"
                              class="btn-text fill-primary min-w-fit md:w-full !flex gap-2 justify-center items-center"
                              icon="file-download"
                            >
                              <Icon name="eye" />
                              <span>
                                Descargar CV
                              </span>
                            </a>
                        </h3>
                        <Divider class="mx-0 " />
                        <div class="p-4 text-center mb-2">
                          <Illustration name="add-files" class="w-full max-h-36 p-2" />
                          <p class="title-lg text-center text-primary mb-1">Actualiza tu curriculum vitae!</p>
                          <p class="title-sm text-neutral40 mb-4">
                            Puedes agregar un archivo en formato PDF. El tamaño máximo admitido es de 5 MB.
                          </p>
                          <FormInputFile
                            accept=".pdf"
                            show-file-size
                            :max-file-size="2242880"
                            @update-files="files = $event"
                          ></FormInputFile>
                        </div>
                      </section>
                    </div>
                    <div class="col-md-12">
                      <Button type="button" class="btn btn-filled brand-blue mb-2 shrink-0" @click="addCv">Actualizar</Button>
                      <Button type="button" class="btn-outlined mb-2 shrink-0" @click="$modal.hide('modal_cv')">Cerrar</Button>
                    </div>        
                  </div>
                </modal>
            </ListItemWithDivider>
            <ListItemWithDivider :with-divider="$window.width >= 905">
              <!-- TODO: Implementacion del metodo shareProfile -->
              <Button
                v-if="isApplicantUser"
                class="btn-text fill-error text-error hover:bg-error-container min-w-fit md:w-full"
                icon="round-log-out"
                @click="logout()"
                >Cerrar sesión</Button
              >
            </ListItemWithDivider>
          </div>
          <Divider class="mx-0 md:hidden" />
        </div>
      </div>
      <section class="mt-16 flex justify-center">
        <!-- Ad section -->
        <div v-html="amp_mobile" class="md:hidden"></div>
        <div v-html="amp_desktop" class="hidden md:block"></div>
      </section>
    </aside>
    <!-- Main Container -->
    <main class="md:col-start-6 md:col-end-14 xl:col-span-8">
      <div class="flex flex-col gap-2 md:gap-0 md:rounded-lg md:border md:border-outline/16">
        <ProfileBanner
          v-if="$window.width >= 905"
          class="hidden md:block"
          :src="user.banner_image"
        />
        <section class="flex flex-col gap-2 md:p-7 md:pt-4">
          <!-- Title container -->
          <div class="py-4 md:py-0">
            <div class="flex items-center justify-between">
              <h2 :class="['headline-sm', isApplicantUser ? 'text-start' : 'w-full text-center']">
                Perfil
              </h2>
              <ButtonIcon
                v-if="isApplicantUser && $window.width <= 905"
                icon="settings-filled"
                class="btn-filled brand-green fill-white md:hidden"
              />
              <Button
                v-else-if="isApplicantUser && $window.width >= 905 && false"
                class="hidden btn-filled brand-green fill-white md:block"
                icon="settings-filled"
              >
                Administrar
              </Button>
              <!-- <Button
                class="hidden btn-filled brand-green fill-white md:block"
                icon="settings-filled">
                Editar
              </Button> -->
            </div>
            <Divider class="hidden md:block md:!w-auto md:!mx-0" />
          </div>
          <div class="flex flex-col gap-2 md:gap-6 md:py-4">
            <ProfileSection class="pb-2">
              <template #title>Información general</template>
              <template #content>
                <!-- Personal info chips -->
                <div class="flex gap-1 flex-wrap">
                  <ChipListItem :icon="genderChip.icon" configuration="outlined" size="small">
                    {{ genderChip.label }}
                  </ChipListItem>
                  <ChipListItem
                    v-for="(chip, key) in profileChips"
                    :key="key"
                    configuration="outlined"
                    size="small"
                    :icon="chip.icon"
                  >
                    {{ chip.label }}
                  </ChipListItem>
                </div>
                <p v-if="user.description" class="body-lg pt-2">
                  {{ user.description }}
                </p>
              </template>
            </ProfileSection>
            <ProfileSection class="pb-2">
              <template #title>
                <div class="flex items-center justify-between">
                  <h2 :class="['headline-sm', isApplicantUser ? 'text-start' : 'w-full text-center']">
                    Habilidades
                  </h2>
                  <Button class="btn-outlined mb-2 shrink-0" icon="add" @click="$modal.show('modal_AddRecord')">Agregar</Button>
                </div>
              </template>
              <template #content>
                <div class="flex flex-wrap gap-1">
                  <ChipListItem
                    v-for="skill in user.skills"
                    :key="skill.id"
                    icon="close"
                    @click-icon="deleteSkill(skill.id)"
                    configuration="outlined"
                    size="small"
                  >
                    {{ skill.title }}
                  </ChipListItem>
                </div>
                <modal
                  name="modal_AddRecord"
                  height="auto"
                  :clickToClose="false"
                  reset
                  adaptive
                  scrollable
                  classes="!surface-1 md:!rounded-xl !overflow-y-auto"
                  @close="$modal.hide('modal_AddRecord')"
                >
                  <div class="card row p-2">    
                    <div class="col-md-12">
                      <h2>Agregar Habilidades</h2>
                      <FormInputText
                        id="skill_input"
                        name="skill_input"
                        v-model="record"
                        class="w-full"
                        placeholder="Ej: Excel avanzado, Marketing digital, Power BI..."/>
                    </div>
                    <div class="col-md-12">
                      <Button type="button" class="btn btn-filled brand-blue mb-2 shrink-0" @click="addRecord">Agregar</Button>
                      <Button type="button" class="btn-outlined mb-2 shrink-0" @click="$modal.hide('modal_AddRecord')">Cerrar</Button>
                    </div>        
                  </div>
                </modal>
              </template>
            </ProfileSection>
            <ProfileSection class="pb-2">
              <template #title>                
                <div class="flex items-center justify-between">
                  <h2 :class="['headline-sm', isApplicantUser ? 'text-start' : 'w-full text-center']">
                    Idiomas
                  </h2>
                  <Button class="btn-outlined mb-2 shrink-0" icon="add" @click="$modal.show('modal_AddLanguages')">Agregar</Button>
                </div>
              </template>
              <template #content>
                <div class="flex flex-wrap gap-1">
                  <ChipListItem
                    v-for="item in user.languages"
                    :key="item.id"
                    configuration="outlined"
                    icon="close"
                    size="small"
                    @click-icon="deleteLanguage(item.id)"
                  >
                    {{ item.language }}: <span class="font-normal"> {{ item.level }} </span>
                  </ChipListItem>
                </div>
                <modal
                  name="modal_AddLanguages"
                  height="auto"
                  :clickToClose="false"
                  reset
                  adaptive
                  scrollable
                  classes="!surface-1 md:!rounded-xl !overflow-y-auto"
                  @close="$modal.hide('modal_AddLanguages')"
                >
                  <div class="card row p-2">    
                    <div class="col-md-12">
                      <FormSelect
                        id="language_id"
                        v-model="language_id"
                        label="Idioma"
                        :required="true"
                        :error="errors.language_id"
                      >
                        <option value="0" disabled>Seleccionar</option>
                        <option v-for="option in language_options" :key="option.id" :value="option.id">
                          {{ option.name }}
                        </option>
                      </FormSelect>
                      <FormSelect
                        id="language_proficiency_level_id"
                        v-model="language_proficiency_level_id"
                        label="Nivel de competencia"
                        hint="Selecciona tu grado de competencia lingüística en el idioma"
                        :required="true"
                        :error="errors.language_proficiency_level_id"
                      >
                        <option value="0" disabled>Seleccionar</option>
                        <option v-for="option in language_proficiency_options" :key="option.id" :value="option.id">
                          {{ option.name }}
                        </option>
                      </FormSelect>
                    </div>
                    <div class="col-md-12">
                      <Button type="button" class="btn btn-filled brand-blue mb-2 shrink-0" @click="addLanguages">Agregar</Button>
                      <Button type="button" class="btn-outlined mb-2 shrink-0" @click="$modal.hide('modal_AddLanguages')">Cerrar</Button>
                    </div>         
                  </div>
                </modal>
              </template>
            </ProfileSection>
            <ProfileSection>
              <template #title>                
                <div class="flex items-center justify-between">
                  <h2 :class="['headline-sm', isApplicantUser ? 'text-start' : 'w-full text-center']">
                    Experiencia Laboral
                  </h2>
                  <Button class="btn-outlined mb-2 shrink-0" icon="add" @click="$modal.show('modal_AddJob')">Agregar</Button>
                </div>                
              </template>
              <template #content>
                <ProfileListItemDelete :ruta='"job_delete"' v-for="item in experience" :key="item.id" :item="item" />
                <modal
                  name="modal_AddJob"
                  height="auto"
                  :clickToClose="false"
                  :reset="true"
                  :adaptive="true"
                  :scrollable="true"
                  classes="!surface-1 md:!rounded-xl !overflow-y-auto">
                  <div class="card row p-2">    
                    <div class="col-md-12">
                      <FormInputText
                        id="job_title"
                        v-model="form.job_title"
                        label="Cargo desempeñado"
                        required
                        :error="errors.job_title"
                      />
                      <FormInputText
                        id="company_name"
                        v-model="form.company_name"
                        label="Nombre de la empresa"
                        required
                        :error="errors.company_name"
                      />
                      <FormTextarea
                        id="description"
                        v-model="form.description"
                        label="Descripción"
                        hint="Incluye un breve texto que describa tus actividades y responsabilidades en el cargo"
                        :error="errors.description"
                        :maxlength="500"
                      />
                      <FormSelect
                        id="is_currently_active"
                        v-model="form.is_currently_active"
                        label="¿Es tu cargo actual?"
                        required>
                        <option  value='1'>Sí</option>
                        <option  value='0'>No</option>
                      </FormSelect>
                      <div class="flex flex-col md:flex-row md:gap-2">
                        <div class="flex-grow">
                          <FormInputText
                            id="start_date"
                            v-model="form.start_date"
                            type="date"
                            label="Fecha inicio"
                            required
                            :error="errors.start_date"
                          />
                        </div>
                        <div v-if="form.is_currently_active == 0" class="flex-grow">
                          <FormInputText
                            id="finish_date"
                            v-model="form.finish_date"
                            type="date"
                            label="Fecha finalización"
                            required
                            :error="errors.finish_date"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="col-md-12">
                      <Button type="button" class="btn btn-filled brand-blue mb-2 shrink-0" @click="addJob">Agregar</Button>
                      <Button type="button" class="btn-outlined mb-2 shrink-0" @click="$modal.hide('modal_AddJob')">Cerrar</Button>
                    </div>         
                  </div>
                </modal>
              </template>
            </ProfileSection>
            <ProfileSection>
              <template #title>              
                <div class="flex items-center justify-between">
                  <h2 :class="['headline-sm', isApplicantUser ? 'text-start' : 'w-full text-center']">
                    Educación
                  </h2>
                  <Button class="btn-outlined mb-2 shrink-0" icon="add" @click="$modal.show('modal_AddEducation')">Agregar</Button>
                </div>
              </template>
              <template #content>
                <ProfileListItemDelete :ruta='"education_delete"' v-for="item in education" :key="item.id" :item="item" />
                <modal
                  name="modal_AddEducation"
                  height="auto"
                  :clickToClose="false"
                  :reset="true"
                  :adaptive="true"
                  :scrollable="true"
                  classes="!surface-1 md:!rounded-xl !overflow-y-auto">
                  <div class="card row p-2">    
                    <div class="col-md-12">
                      <FormInputText
                        id="title"
                        v-model="form_education.title"
                        label="Título"
                        :required="true"
                        :error="errors.title"
                      />

                      <FormSelect
                        id="educational_degree"
                        v-model="form_education.educational_degree_id"
                        label="Grado de estudio"
                        :required="true"
                        :error="errors.educational_degree"
                      >
                        <option value="0" disabled>Seleccionar</option>
                        <option v-for="option in educational_degrees_options" :key="option.id" :value="option.id">
                          {{ option.name }}
                        </option>
                      </FormSelect>

                      <FormSelect
                        id="study_field"
                        v-model="form_education.study_field_id"
                        label="Campo de estudio"
                        :required="true"
                        :error="errors.study_field"
                      >
                        <option value="0" disabled>Seleccionar</option>
                        <option v-for="option in study_fields_options" :key="option.id" :value="option.id">
                          {{ option.name }}
                        </option>
                      </FormSelect>

                      <FormSelect
                        id="educational_institution"
                        v-model="form_education.educational_institution_id"
                        label="Institución Educativa"
                        :required="true"
                        :error="errors.educational_institution"
                      >
                        <option value="0" disabled>Seleccionar</option>
                        <option
                          v-for="option in educational_institutions_options"
                          :key="option.id"
                          :value="option.id"
                        >
                          {{ option.name }}
                        </option>
                      </FormSelect>
                      <FormInputText
                        v-if="form_education.educational_institution_id == 5"
                        id="educational_institution_name_aux"
                        v-model="form_education.educational_institution_name_aux"
                        label="Nombre de tu institución educativa"
                        hint="Ingresa el nombre de tu institución educativa que no aparece en el listado"
                        :required="true"
                        :error="errors.educational_institution_name_aux"
                      />

                      <FormTextarea
                        id="description"
                        v-model="form_education.description"
                        label="Descripción"
                        hint="Puedes incluir una breve descripción de las actividades que realizaste en tu institución"
                        :maxlength="300"
                      />
                      <div class="flex flex-col md:flex-row md:gap-2">
                        <div class="flex-grow">
                          <FormInputText
                            id="start_date"
                            v-model="form_education.start_date"
                            type="date"
                            label="Fecha inicio"
                            :required="true"
                            :error="errors.start_date"
                          />
                        </div>
                        <div class="flex-grow">
                          <FormInputText
                            id="finish_date"
                            v-model="form_education.finish_date"
                            type="date"
                            label="Fecha finalización"
                            :error="errors.finish_date"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="col-md-12">
                      <Button type="button" class="btn btn-filled brand-blue mb-2 shrink-0" @click="addEducation">Agregar</Button>
                      <Button type="button" class="btn-outlined mb-2 shrink-0" @click="$modal.hide('modal_AddEducation')">Cerrar</Button>
                    </div>         
                  </div>
                </modal>
              </template>
            </ProfileSection>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import getPropertiesIfExistFromDataObjectMixin from '@/getPropertiesIfExistFromDataObject.mixin.js'
import evalUserProfileTypeMixin from '@/evalUserProfileType.mixin.js'
import { Head, Link } from '@inertiajs/inertia-vue'
import Layout from '@/Layouts/Layout'
import Icon from '@/Components/Icon'
import Button from '@/Components/Button'
import ButtonIcon from '@/Components/ButtonIcon'
import Divider from '@/Components/Divider'
import ProfileBanner from '@/Components/ProfileBanner'
import ProfilePicture from '@/Components/ProfilePicture'
import ProfileSection from '@/Components/ProfileSection'
import ListItemWithDivider from '@/Components/ListItemWithDivider'
import ChipListItem from '@/Components/ChipListItem'
import ProfileListItem from '@/Components/ProfileListItem'
import ProfileListItemDelete from '@/Components/ProfileListItemDelete'
import FormInputText from '@/Components/FormInputText'
import LanguageForm from '@/Components/Perfil/LanguageForm.vue'
import FormSelect from '@/Components/FormSelect'
import FormTextarea from '@/Components/FormTextarea'
import FormInputFile from '@/Components/FormInputFile'
import Illustration from '@/Components/Illustration'

export default {
  name: 'ApplicantProfile',
  layout: Layout,
  components: {
    Head,
    Link,
    Icon,
    Button,
    ButtonIcon,
    FormSelect,
    Divider,
    FormInputText,
    FormInputFile,
    FormTextarea,
    ProfileBanner,
    ProfilePicture,
    Illustration,
    ProfileSection,
    LanguageForm,
    ListItemWithDivider,
    ChipListItem,
    ProfileListItem,
    ProfileListItemDelete
  },
  mixins: [getPropertiesIfExistFromDataObjectMixin, evalUserProfileTypeMixin],
  props: {
    errors: {
      type: Object,
    },
    user: {
      type: Object,
      required: true,
    },
    educational_degrees_options: Array,
    educational_institutions_options: Array,
    study_fields_options: Array,
    language_options: Array,
    language_proficiency_options: Array,
  },
  data() {
    return {
      record: '',
      language_id: 0,
      language_proficiency_level_id: 0,
      cv: '',
      files: [],
      form: {
        job_title: '',
        description: '',
        company_name: '',
        is_currently_active: 0,
        start_date: '',
        finish_date: '',
      },
      form_education: {
        title: '',
        description: '',
        educational_institution_id: 0,
        educational_institution_name_aux: '',
        study_field_id: 0,
        educational_degree_id: 0,
        start_date: '',
        finish_date: '',
        is_finished: this.finish_date !== '' && this.finish_date !== this.start_date,
      },
      currentTab: 'main',
      amp_desktop:
        '<amp-ad width=300 height=600 type="doubleclick" data-slot="/4483046/lpg-dsk-empleo" data-multi-size="300x600" data-multi-size-validation="false" data-loading-strategy="3" data-enable-refresh="30"></amp-ad>',
      amp_mobile:
        '<amp-ad width=320 height=100 type="doubleclick" data-slot="/4483046/lpg-mov-empleo" data-multi-size="320x100" data-multi-size-validation="false" data-loading-strategy="3" data-enable-refresh="30"></amp-ad>',
      
    }
  },
  computed: {
    experience() {
      if (!this.user.hasOwnProperty('experience')) return []
      else
        return this.user.experience.map(item => {
          return {
            id: item.id,
            description: item.description,
            title: item.job_title,
            subtitle: item.company_name,
            started: this.parseDate(item.start_date),
            finished: this.parseDate(item.finish_date),
          }
        })
    },
    education() {
      if (!this.user.hasOwnProperty('education')) return []
      else
        return this.user.education.map(item => {
          return {
            id: item.id,
            description: item.description,
            title: item.title,
            subtitle: item.institution,
            started: this.parseDate(item.start_date),
            finished: this.parseDate(item.finish_date),
          }
        })
    },
    fullname() {
      if (this.user.hasOwnProperty('fullname')) return this.user.fullname
      else return `${this.user.name} ${this.user.lastname}`.trim()
    },
    age() {
      if (this.user.hasOwnProperty('age')) return this.user.age
      else {
        const now = new Date(),
          birthdate = new Date(this.user.date_of_birth),
          difference = now - birthdate
        return Math.floor(difference / 31557600000)
      }
    },
    userSocialMedia() {
      return this.getPropertiesIfExistFromDataObject('user', {
        fb_url: { icon: 'round-facebook' },
        linkedin_url: { icon: 'linkedin' },
      })
    },
    profileChips() {
      return this.getPropertiesIfExistFromDataObject('user', {
        date_of_birth: {
          label: `${this.age} años`,
          icon: 'today',
        },
        has_motorcycle_driving_license: {
          label: 'Licencia motocicleta',
          icon: 'baseline-motorcycle',
        },
        has_vehicle_ownership: {
          label: 'Posee vehículo',
          icon: 'directions-car',
        },
        has_driving_license: {
          label: 'Posee licencia',
          icon: 'directions-car',
        },
      }).filter(item => typeof item.found_value !== 'boolean' || item.found_value === true)
    },
    genderChip() {
      if (this.user.gender === 'Femenino') {
        return {
          label: 'Mujer',
          icon: 'baseline-female',
        }
      } else if (this.user.gender === 'Masculino') {
        return {
          label: 'Hombre',
          icon: 'baseline-male',
        }
      } else if (this.user.gender === 'Otro') {
        return {
          label: 'Trans',
          icon: 'baseline-transgender',
        }
      }
    },
    currentTabComponent() {
      return `CompanyProfile${this.currentTab.charAt(0).toUpperCase() +
        this.currentTab.slice(1)}Tab`
    },
  },
  watch: {
    files: {
      deep: true,
      handler(value) {
        this.$emit('update-content', 'cv_file', value[0])
      },
    },
  },
  methods: {
    logout() {
      this.$inertia.delete(this.$route('logout'))
    },
    cleanContent() {
      this.record = ''
      this.language_id = 0,
      this.language_proficiency_level_id = 0
      this.form = {
        job_title: '',
        description: '',
        company_name: '',
        is_currently_active: '',
        start_date: '',
        finish_date: '',
      }
      
      this.form_education = {
        title: '',
        description: '',
        educational_institution_id: 0,
        educational_institution_name_aux: '',
        study_field_id: 0,
        educational_degree_id: 0,
        start_date: '',
        finish_date: '',
        is_finished: this.finish_date !== '' && this.finish_date !== this.start_date,
      },
      this.files = []
    },
    deleteSkill(key) {
      console.log(key)
      this.$swal
        .fire({
          title: 'Confirmación:',
          text: '¿Estás seguro que quieres borrar ese dato?',
          showConfirmButton: true,
          showCancelButton: true,
        })
        .then(result => {
          if (result.isConfirmed) {
            this.$inertia.post(this.$route('profile.skill_delete'), { id: key }, {
              preserveState: true,
              onError: errors => {
                this.$swal.fire({
                  title: '¡Error!',
                  text: 'Parece que ha ocurrido un error. Revisa los datos e intenta de nuevo.',
                })
              },
              onSuccess: () => {
                this.$swal.fire({
                  title: 'Habilidad Elimniada!',
                  text: 'Tus datos se guardaron con éxito',
                })
              },
            })
          }
        })
    },
    deleteLanguage(key) {
      console.log(key)
      this.$swal
        .fire({
          title: 'Confirmación:',
          text: '¿Estás seguro que quieres borrar ese dato?',
          showConfirmButton: true,
          showCancelButton: true,
        })
        .then(result => {
          if (result.isConfirmed) {
            this.$inertia.post(this.$route('profile.languages_delete'), { id: key }, {
              preserveState: true,
              onError: errors => {
                this.$swal.fire({
                  title: '¡Error!',
                  text: 'Parece que ha ocurrido un error. Revisa los datos e intenta de nuevo.',
                })
              },
              onSuccess: () => {
                this.$swal.fire({
                  title: 'Idioma Elimniado!',
                  text: 'Tus datos se guardaron con éxito',
                })
              },
            })
          }
        })
    },
    addRecord() {
      this.$swal.fire({
        title: 'Guardando cambios...',
        html: `
            <div class="flex gap-2 items-center">
              <svg v-else-if="name === 'loading'" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" role="img" width="24" height="24" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet" ><path fill="currentColor" d="M12 2A10 10 0 1 0 22 12A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8A8 8 0 0 1 12 20Z" opacity=".5"/><path fill="currentColor" d="M20 12h2A10 10 0 0 0 12 2V4A8 8 0 0 1 20 12Z"><animateTransform attributeName="transform" dur="1s" from="0 12 12" repeatCount="indefinite" to="360 12 12" type="rotate"/></path></svg>
              <p>Espera un momento, por favor.</p>
            </div>
          `,
        showConfirmButton: false,
      })

      this.$inertia.post(this.$route('profile.update.skill'), { skill: [{ title: this.record }] }, {
        preserveState: true,
        onError: errors => {
          this.$swal.fire({
            title: '¡Error!',
            text: 'Parece que ha ocurrido un error. Revisa los datos e intenta de nuevo.',
          })
        },
        onSuccess: () => {
          this.$swal.fire({
            title: '¡Habilidad Agregada!',
            text: 'Tus datos se guardaron con éxito',
          })
          this.cleanContent()
        },
      })
    },
    addLanguages() {
      this.$swal.fire({
        title: 'Guardando cambios...',
        html: `
            <div class="flex gap-2 items-center">
              <svg v-else-if="name === 'loading'" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" role="img" width="24" height="24" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet" ><path fill="currentColor" d="M12 2A10 10 0 1 0 22 12A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8A8 8 0 0 1 12 20Z" opacity=".5"/><path fill="currentColor" d="M20 12h2A10 10 0 0 0 12 2V4A8 8 0 0 1 20 12Z"><animateTransform attributeName="transform" dur="1s" from="0 12 12" repeatCount="indefinite" to="360 12 12" type="rotate"/></path></svg>
              <p>Espera un momento, por favor.</p>
            </div>
          `,
        showConfirmButton: false,
      })

      this.$inertia.post(this.$route('profile.update.languages'), { languages: [{ language_id: parseInt(this.language_id) ,language_proficiency_level_id: parseInt(this.language_proficiency_level_id) }] }, {
        preserveState: true,
        onError: errors => {
          this.$swal.fire({
            title: '¡Error!',
            text: 'Parece que ha ocurrido un error. Revisa los datos e intenta de nuevo.',
          })
        },
        onSuccess: () => {
          this.$swal.fire({
            title: 'Lenguaje Agregada!',
            text: 'Tus datos se guardaron con éxito',
          })
          this.cleanContent()
        },
      })
    },
    addJob() {
      this.$swal.fire({
        title: 'Guardando cambios...',
        html: `
            <div class="flex gap-2 items-center">
              <svg v-else-if="name === 'loading'" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" role="img" width="24" height="24" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet" ><path fill="currentColor" d="M12 2A10 10 0 1 0 22 12A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8A8 8 0 0 1 12 20Z" opacity=".5"/><path fill="currentColor" d="M20 12h2A10 10 0 0 0 12 2V4A8 8 0 0 1 20 12Z"><animateTransform attributeName="transform" dur="1s" from="0 12 12" repeatCount="indefinite" to="360 12 12" type="rotate"/></path></svg>
              <p>Espera un momento, por favor.</p>
            </div>
          `,
        showConfirmButton: false,
      })

      this.$inertia.post(this.$route('profile.update.job'), { job_experience: [this.form] }, {
        preserveState: true,
        onError: errors => {
          this.$swal.fire({
            title: '¡Error!',
            text: 'Parece que ha ocurrido un error. Revisa los datos e intenta de nuevo.',
          })
        },
        onSuccess: () => {
          this.$swal.fire({
            title: 'Experiencia Laboral Agregada!',
            text: 'Tus datos se guardaron con éxito',
          })
          this.cleanContent()
        },
      })
    },
    addEducation() {
      this.$swal.fire({
        title: 'Guardando cambios...',
        html: `
            <div class="flex gap-2 items-center">
              <svg v-else-if="name === 'loading'" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" role="img" width="24" height="24" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet" ><path fill="currentColor" d="M12 2A10 10 0 1 0 22 12A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8A8 8 0 0 1 12 20Z" opacity=".5"/><path fill="currentColor" d="M20 12h2A10 10 0 0 0 12 2V4A8 8 0 0 1 20 12Z"><animateTransform attributeName="transform" dur="1s" from="0 12 12" repeatCount="indefinite" to="360 12 12" type="rotate"/></path></svg>
              <p>Espera un momento, por favor.</p>
            </div>
          `,
        showConfirmButton: false,
      })

      this.$inertia.post(this.$route('profile.update.education'), { education: [this.form_education] }, {
        preserveState: true,
        onError: errors => {
          this.$swal.fire({
            title: '¡Error!',
            text: 'Parece que ha ocurrido un error. Revisa los datos e intenta de nuevo.',
          })
        },
        onSuccess: () => {
          this.$swal.fire({
            title: 'Registro Agregado!',
            text: 'Tus datos se guardaron con éxito',
          })
          this.cleanContent()
        },
      })
    },
    addCv() {
      console.log(this.files[0])
      this.$swal.fire({
        title: 'Guardando cambios...',
        html: `
            <div class="flex gap-2 items-center">
              <svg v-else-if="name === 'loading'" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" role="img" width="24" height="24" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet" ><path fill="currentColor" d="M12 2A10 10 0 1 0 22 12A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8A8 8 0 0 1 12 20Z" opacity=".5"/><path fill="currentColor" d="M20 12h2A10 10 0 0 0 12 2V4A8 8 0 0 1 20 12Z"><animateTransform attributeName="transform" dur="1s" from="0 12 12" repeatCount="indefinite" to="360 12 12" type="rotate"/></path></svg>
              <p>Espera un momento, por favor.</p>
            </div>
          `,
        showConfirmButton: false,
      })

      this.$inertia.post(this.$route('profile.update.curriculum'), { cv_file: this.files[0] }, {
        preserveState: true,
        onError: errors => {
          this.$swal.fire({
            title: '¡Error!',
            text: 'Parece que ha ocurrido un error. Revisa los datos e intenta de nuevo.',
          })
        },
        onSuccess: () => {
          this.$swal.fire({
            title: 'Cv Actualizado!',
            text: 'Tus datos se guardaron con éxito',
          })
          this.cleanContent()
        },
      })
    },
    otherEducationalInstitutionIndex() {
      return this.educational_institutions_options.findIndex(
        item => item.name === 'No encuentro mi institución',
      )
    },
    otherEducationalInstitution() {
      return this.educational_institutions_options[this.otherEducationalInstitutionIndex]
    },
    educational_institutions_options_ordered() {
      // Make a shallow copy of the options array
      let orderedOptions = this.educational_institutions_options.slice()
      // Delete 'No encuentro mi institucion' on whatever place it is
      orderedOptions.splice(this.otherEducationalInstitutionIndex, 1)
      // Place 'No encuentro mi institucion' option on first place
      orderedOptions.unshift(this.otherEducationalInstitution)

      return orderedOptions
    },
    parseDate(dateString) {
      if (typeof dateString !== 'string') return dateString
      const date = new Date(dateString)
      return `${date
        .getDate()
        .toString()
        .padStart(2, '0')}/${(date.getMonth() + 1)
        .toString()
        .padStart(2, '0')}/${date.getFullYear()}`
    },
  },
  mounted() {
    this.$nextTick(function() {
      window.scrollTo(0, 0)
    })
  },
}
</script>
